import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Banknote, Timer, Loader2 } from "lucide-react";

export default function Settlements() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/global-admin/settlements/clearinghouse").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const mature = async (days) => {
    setBusy(true);
    try {
      const r = await api.post(`/settlements/escrow/mature${days === 0 ? "?override_escrow_days=0" : ""}`);
      toast.success(`Matured ${r.data.orders_matured} orders · ${r.data.creator_ledgers_promoted} creator entries`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const processBatch = async () => {
    setBusy(true);
    try {
      const r = await api.post("/global-admin/settlements/process-batch");
      if (r.data.pending_config) toast.warning(r.data.message);
      else toast.success(`Settled ${r.data.settled} payouts`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const totalReady = rows.reduce((s, r) => s + r.ready_amount, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="display text-2xl font-bold flex items-center gap-2"><Banknote size={24} /> Settlement Clearinghouse</h2>
        <div className="flex gap-2">
          <button className="nb-btn nb-btn-ghost text-xs" onClick={() => mature(7)} disabled={busy} data-testid="settle-mature"><Timer size={14} /> Run Maturation (7d)</button>
          <button className="nb-btn nb-btn-ghost text-xs" onClick={() => mature(0)} disabled={busy} data-testid="settle-mature-dev">Dev: Mature Now (0d)</button>
          <button className="nb-btn" onClick={processBatch} disabled={busy || !rows.length} data-testid="settle-process">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Process Batch Payout"}
          </button>
        </div>
      </div>

      <div className="nb-card-sm p-3 bg-[#86EFAC] border-2 border-black inline-block">
        <span className="text-xs">Ready for settlement</span>
        <div className="display text-2xl font-black mono" data-testid="settle-total">₹{totalReady.toFixed(2)}</div>
      </div>

      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Type</th><th>Payee</th><th>Currency</th><th>Entries</th><th>Ready Amount</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.party_type}-${r.party_id}-${i}`} data-testid={`settle-row-${r.party_id}`}>
                <td><span className={`nb-badge ${r.party_type === "VENDOR" ? "bg-[#93C5FD]" : "bg-[#C4B5FD]"}`}>{r.party_type}</span></td>
                <td className="font-bold">{r.party_name || r.party_id.slice(0, 8)}</td>
                <td className="mono text-xs">{r.currency}</td>
                <td className="mono">{r.entry_count}</td>
                <td className="mono font-bold">₹{r.ready_amount.toFixed(2)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500">No funds ready for settlement. Run maturation to release delivered/creator earnings.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs opacity-60">Payouts use Razorpay Route (INR) / Stripe Connect (USD). If the platform payout account isn't configured, entries move to <b>PAYOUT_PENDING_CONFIG</b> without breaking the batch.</p>
    </div>
  );
}
